import { Box, Card, Grid, Typography } from "@mui/material";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { appUrl, tiktokClientId } from "../../common/api/config";
import tiktokLogo from "../../common/assets/img/platform_logos/tiktok.webp";
import Button from "../../common/components/Button";
import { SPACING } from "../../common/constants/spacing";
import { requestAccessTokenFromTikTok } from "../api/tiktok";

// Generate a random string for PKCE code_verifier
const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

// Base64URL encode without padding
const base64URLEncode = (buffer) => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
};

// Generate SHA256 hash
const sha256 = async (plain) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(hash);
};

// Generate code_challenge from code_verifier
const generateCodeChallenge = async (codeVerifier) => {
  const hashed = await sha256(codeVerifier);
  return base64URLEncode(hashed);
};

const openTikTokOAuthPopup = async () => {
  const redirectUri = `${appUrl}/tiktok-oauth-callback`;

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Store code_verifier in sessionStorage to retrieve later
  sessionStorage.setItem("tiktok_code_verifier", codeVerifier);

  const scope = "user.info.basic,video.list";
  const state = "tiktok-oauth";
  const oauthUrl = `https://www.tiktok.com/v2/auth/authorize?client_key=${tiktokClientId}&redirect_uri=${encodeURIComponent(
    redirectUri,
  )}&response_type=code&scope=${encodeURIComponent(scope)}&state=${state}&code_challenge=${codeChallenge}&code_challenge_method=S256`;

  const width = 500;
  const height = 600;
  const left = (window.innerWidth - width) / 2;
  const top = (window.innerHeight - height) / 2;

  window.open(
    oauthUrl,
    "TikTokOAuth",
    `width=${width},height=${height},top=${top},left=${left}`,
  );
};

export const LinkTikTokCard = ({ createSource }) => {
  const { t } = useTranslation("sources");

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.source === "tiktok-oauth" && event.data.code) {
        const authCode = event.data.code;

        // Retrieve the code_verifier from sessionStorage
        const codeVerifier = sessionStorage.getItem("tiktok_code_verifier");

        if (!codeVerifier) {
          console.error("code_verifier not found in sessionStorage");
          return;
        }

        // Pass code_verifier to the token exchange
        const tokenData = await requestAccessTokenFromTikTok(
          authCode,
          codeVerifier,
        );

        // Clean up sessionStorage
        sessionStorage.removeItem("tiktok_code_verifier");

        const expiresAt = Date.now() + tokenData.expires_in * 1000;
        const source = {
          account_name: "Account Name",
          platform: "tiktok",
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          token_expires_at: new Date(expiresAt).toISOString(),
        };
        try {
          await createSource(source);
        } catch (error) {
          console.error("Error creating data source:", error);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid size={{ xs: 12, md: 4 }}>
      <Card variant="outlined" sx={{ p: 3, height: "100%", mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%",
          }}
        >
          <Box>
            <img
              src={tiktokLogo}
              alt="TikTok Logo"
              style={{ height: "70px", objectFit: "contain", marginBottom: 10 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", mb: SPACING.xs }}
            >
              TikTok
            </Typography>
            <Typography variant="bodySm" sx={{ color: "text.secondary" }}>
              {t("platforms.tiktok.description")}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            sx={{ mt: 3 }}
            onClick={openTikTokOAuthPopup}
            fullWidth
          >
            {t("platforms.tiktok.buttonText")}
          </Button>
        </Box>
      </Card>
    </Grid>
  );
};
